import { authFetch } from '../../lib/authFetch';
import { PO, POShipmentData, Style } from '../../types/data';
import { PURCHASE_ORDERS, SHIPPING_LABEL, SUBMIT_SHIPPING } from '../paths';

export type PurchaseOrdersState = 'Confirmed' | 'Unconfirmed';

export type SubmitShipmentParams = string | string[][] | Record<string, string> | URLSearchParams;

export type POShippingInfo = {
  fromAddress: {
    company: string;
    country: string;
    name: string;
    street1: string;
    street2: string;
    city: string;
    state: string;
    zipCode: string;
    phone: string;
  };
  toAddress: {
    company: string;
    name: string;
    street1: string;
    street2: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
    phone: string;
  };
  cartons: [
    {
      parcel: {
        massUnit: string;
        weight: number;
      };
    }
  ];
};

type TokenProvider = () => Promise<string>;

export interface ApiClient {
  getPurchaseOrders: (params: {
    page: number;
    size: number;
    search: string;
    state: PurchaseOrdersState;
  }) => Promise<POShipmentData>;
  getPurchaseOrder: (poNumber: string) => Promise<PO>;
  getPurchaseOrderStyles: (poNumber: string) => Promise<Style[]>;
  getPurchaseOrderPdf: (poNumber: string) => Promise<Blob>;
  submitShipping: (formData: SubmitShipmentParams) => Promise<POShipmentData>;
  submitShippingLabel: (params: { asnNumber: number; data: POShippingInfo }) => Promise<unknown>;
}

export const createV1ApiClient = (getAccessTokenSilently: TokenProvider): ApiClient => ({
  getPurchaseOrders: async ({ page, size, search, state }) => {
    const token = await getAccessTokenSilently();
    const url = `${PURCHASE_ORDERS}?page=${page}&size=${size}&searchQuery=${encodeURIComponent(search)}&state=${state}`;
    return authFetch<POShipmentData>(url, token);
  },
  getPurchaseOrder: async poNumber => {
    const token = await getAccessTokenSilently();
    return authFetch<PO>(`${PURCHASE_ORDERS}/${poNumber}`, token);
  },
  getPurchaseOrderStyles: async poNumber => {
    const token = await getAccessTokenSilently();
    return authFetch<Style[]>(`${PURCHASE_ORDERS}/${poNumber}/styles`, token);
  },
  getPurchaseOrderPdf: async poNumber => {
    const token = await getAccessTokenSilently();
    return authFetch<Blob>(`${PURCHASE_ORDERS}/${poNumber}.pdf`, token, {
      contentType: 'application/pdf',
      responseType: 'blob'
    });
  },
  submitShipping: async formData => {
    const token = await getAccessTokenSilently();
    return authFetch<POShipmentData>(SUBMIT_SHIPPING, token, {
      method: 'POST',
      body: new URLSearchParams(formData),
      contentType: 'application/x-www-form-urlencoded'
    });
  },
  submitShippingLabel: async ({ asnNumber, data }) => {
    const token = await getAccessTokenSilently();
    return authFetch(`${SHIPPING_LABEL}?asnNumber=${asnNumber}`, token, {
      method: 'POST',
      body: data
    });
  }
});

export const createV2ApiClient = (getAccessTokenSilently: TokenProvider): ApiClient =>
  createV1ApiClient(getAccessTokenSilently);

export const createApiClient = (getAccessTokenSilently: TokenProvider): ApiClient =>
  createV1ApiClient(getAccessTokenSilently);
